import {
  PopoverPosition,
  Position,
  Radio,
  RadioGroup,
} from '@blueprintjs/core';
import * as React from 'react';
import { Tooltip } from 'vet-bones/components';

import { InlineEditableLabel } from 'src/components/InlineEditableLabel';
import { ModelSelector } from 'src/components/ModelSelector';
import {
  TEST_IDS,
  getPublishProjectDrawerCreationTypeTestId,
} from 'src/constants/testIds';
import {
  CreationType,
  ModelInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { ModelSelectItem } from 'src/selectors/modelItems';
import { replaceableModelItemsSelector } from 'src/selectors/replaceableModelItems';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

type ModelMappingFormProps = {
  databaseId: string;
  existingModel: ModelSelectItem | null;
  handleChangeCreationType: (creationType: CreationType) => void;
  handleSelectExistingModel: (modelItem: ModelSelectItem | null) => void;
  handleSetModelInfo: (
    nextInfo: Partial<
      Pick<ModelInfo, 'creationType' | 'modelName' | 'namedGraphIri'>
    >
  ) => void;
  modelInfo: Pick<ModelInfo, 'creationType' | 'modelName'> & {
    namedGraphIri?: string;
  };
  modelNamePlaceholder: string;
  /** override modelItems used, by default uses replaceableModelItemsSelector */
  modelItems?: ModelSelectItem[];
  namedGraphIriPlaceholder: string;
  validateModelName: (modelName: string) => string;
  validateNamedGraph: (modelName: string) => string;
};

export const ModelMappingForm: React.VFC<ModelMappingFormProps> = ({
  databaseId,
  existingModel,
  handleChangeCreationType,
  handleSelectExistingModel,
  handleSetModelInfo,
  modelInfo,
  modelNamePlaceholder,
  modelItems: modelItemsOverride,
  namedGraphIriPlaceholder,
  validateModelName,
  validateNamedGraph,
}) => {
  const replaceableModelItems = useReduxSelector(
    replaceableModelItemsSelector(databaseId)
  );
  const databaseIds = useReduxSelector((state) => state.databases.databaseIds);

  const modelItems = modelItemsOverride || replaceableModelItems;

  const isCreatingDb = !databaseIds.includes(databaseId);
  const hasExistingModels = modelItems.length;
  let replaceTooltip = '';
  if (!hasExistingModels) {
    replaceTooltip = isCreatingDb
      ? copy.components.publishProjectDrawer.modelConfiguration.modelName
          .noExisting
      : copy.components.publishProjectDrawer.modelConfiguration.modelName
          .noModelsToReplace;
  }

  return (
    <>
      <div className="sd-drawer-margin">
        <Tooltip
          content={replaceTooltip}
          disabled={!replaceTooltip}
          position={PopoverPosition.RIGHT}
        >
          <RadioGroup
            onChange={({
              currentTarget: { value },
            }: React.FormEvent<HTMLInputElement>) =>
              handleChangeCreationType(value as CreationType)
            }
            selectedValue={modelInfo.creationType}
          >
            <Radio
              data-testid={getPublishProjectDrawerCreationTypeTestId(
                CreationType.NEW
              )}
              label={
                copy.components.publishProjectDrawer.modelConfiguration
                  .modelName.create
              }
              value={CreationType.NEW}
            />
            <Radio
              data-testid={getPublishProjectDrawerCreationTypeTestId(
                CreationType.REPLACE
              )}
              disabled={!hasExistingModels}
              labelElement={
                <>
                  {
                    copy.components.publishProjectDrawer.modelConfiguration
                      .modelName.replace
                  }

                  <div className="sd-drawer-selector-menu">
                    <ModelSelector
                      databaseId={databaseId}
                      disabled={modelInfo.creationType !== CreationType.REPLACE}
                      modelItems={modelItems}
                      popoverPosition={Position.BOTTOM_LEFT}
                      selectedModel={existingModel}
                      setSelectedModel={(modelItem) =>
                        handleSelectExistingModel(modelItem)
                      }
                    />
                  </div>
                </>
              }
              value={CreationType.REPLACE}
            />
          </RadioGroup>
        </Tooltip>
      </div>
      <div className="sd-drawer-margin">
        <InlineEditableLabel
          disabled={modelInfo.creationType === CreationType.REPLACE}
          label={
            copy.components.publishProjectDrawer.modelConfiguration.modelName
              .label
          }
          onSetValue={(nextModelName) =>
            handleSetModelInfo({
              modelName: nextModelName,
            })
          }
          onValidate={validateModelName}
          placeholder={modelNamePlaceholder}
          testId={TEST_IDS.publishProjectDrawer.model.modelNameInput}
          value={
            modelInfo.creationType === CreationType.REPLACE && !existingModel
              ? copy.components.publishProjectDrawer.modelConfiguration
                  .modelName.placeholder
              : modelInfo.modelName
          }
        />
        {modelInfo.namedGraphIri === undefined ? null : (
          <InlineEditableLabel
            label={
              copy.components.publishProjectDrawer.modelConfiguration.namedGraph
                .label
            }
            onSetValue={(nextNamedGraphIri) =>
              handleSetModelInfo({
                namedGraphIri: nextNamedGraphIri,
              })
            }
            onValidate={validateNamedGraph}
            placeholder={namedGraphIriPlaceholder}
            testId={TEST_IDS.publishProjectDrawer.model.namedGraphIriInput}
            value={
              modelInfo.creationType === CreationType.REPLACE && !existingModel
                ? copy.components.publishProjectDrawer.modelConfiguration
                    .modelName.placeholder
                : modelInfo.namedGraphIri
            }
          />
        )}
      </div>
    </>
  );
};
